import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "../AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { StatusBadge } from "./Dashboard";
import ChunkedUploader from "../components/ChunkedUploader";
import { QuizBuilder } from "../components/Quiz";

export function TeacherCourses() {
  const { API } = useAuth();
  const [courses, setCourses] = useState([]);
  const navigate = useNavigate();

  const load = () => axios.get(`${API}/courses?mine=true`, { withCredentials: true }).then((r) => setCourses(r.data));
  useEffect(() => { load(); }, []);

  const submit = async (id) => {
    await axios.post(`${API}/courses/${id}/submit`, {}, { withCredentials: true });
    toast.success("Submitted for approval");
    load();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="overline">Teaching</div>
          <h1 className="font-heading text-3xl md:text-4xl font-semibold text-[#0A231C]">My Courses</h1>
        </div>
        <button onClick={() => navigate("/teacher/new-course")} className="btn-primary" data-testid="new-course-btn">+ New course</button>
      </div>
      <div className="card-surface overflow-hidden">
        <table className="lms-table w-full">
          <thead><tr><th>Title</th><th>Category</th><th>Status</th><th className="text-right pr-4">Actions</th></tr></thead>
          <tbody>
            {courses.map((c) => (
              <tr key={c.id} data-testid={`teacher-course-row-${c.id}`}>
                <td className="font-heading font-medium">{c.title}</td>
                <td>{c.category}</td>
                <td><StatusBadge s={c.status} /></td>
                <td className="text-right pr-4">
                  <button onClick={() => navigate(`/teacher/courses/${c.id}/edit`)} className="text-sm mr-3 text-[#0A231C] hover:underline">Edit</button>
                  {c.status === "draft" && (
                    <button onClick={() => submit(c.id)} className="text-sm text-[#D45D3C] hover:underline" data-testid={`submit-${c.id}`}>Submit</button>
                  )}
                </td>
              </tr>
            ))}
            {courses.length === 0 && <tr><td colSpan={4} className="text-center text-sm text-[#5C5C5C] py-8">No courses yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function NewCourse() {
  const { user, API } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ title: "", description: "", category: "Design", duration_minutes: 60, price: 29, subscription_eligible: true,
    cover_image: "https://images.unsplash.com/photo-1488972685288-c3fd157d7c7a?w=800" });
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    axios.get(`${API}/categories`, { withCredentials: true }).then((r) => setCategories(r.data));
  }, []);

  if (user?.role !== "teacher") return <div>Only teachers can create courses.</div>;
  if (user?.teacher_status !== "approved") {
    return <div className="card-surface p-8" data-testid="teacher-not-approved">Your teacher account must be approved by IT Admin before you can create courses.</div>;
  }

  const submit = async (e) => {
    e.preventDefault();
    try {
      const r = await axios.post(`${API}/courses`, form, { withCredentials: true });
      toast.success("Course created (draft)");
      navigate(`/teacher/courses/${r.data.id}/edit`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create");
    }
  };

  return (
    <div className="max-w-3xl">
      <div className="overline">Studio</div>
      <h1 className="font-heading text-3xl font-semibold text-[#0A231C] mb-8">Create a new course</h1>
      <form onSubmit={submit} className="card-surface p-8 space-y-5" data-testid="new-course-form">
        <Field label="Title"><input required className="input" value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} data-testid="course-title-input" /></Field>
        <Field label="Description"><textarea required rows={4} className="input" value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} data-testid="course-description-input" /></Field>
        <div className="grid md:grid-cols-2 gap-5">
          <Field label="Category">
            <select className="input" value={form.category} onChange={(e) => setForm({...form, category: e.target.value})} data-testid="course-category-select">
              {categories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="Duration (minutes)">
            <input type="number" min={0} className="input" value={form.duration_minutes} onChange={(e) => setForm({...form, duration_minutes: parseInt(e.target.value || 0)})} />
          </Field>
        </div>
        <div className="grid md:grid-cols-2 gap-5">
          <Field label="Suggested price (INR)">
            <input type="number" min={0} className="input" value={form.price} onChange={(e) => setForm({...form, price: parseFloat(e.target.value || 0)})} />
          </Field>
          <Field label="Cover image URL">
            <input className="input" value={form.cover_image} onChange={(e) => setForm({...form, cover_image: e.target.value})} />
          </Field>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.subscription_eligible} onChange={(e) => setForm({...form, subscription_eligible: e.target.checked})} />
          Eligible for subscription plan
        </label>
        <div className="pt-4">
          <button type="submit" className="btn-primary" data-testid="create-course-submit">Create draft</button>
        </div>
      </form>
      <style>{`.input{width:100%;padding:0.65rem 0.9rem;border:1px solid #E5E3D8;border-radius:8px;background:white;outline:none;font-family:inherit}.input:focus{border-color:#0A231C}`}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="overline mb-1">{label}</div>
      {children}
    </label>
  );
}

export function EditCourse() {
  const { API, user } = useAuth();
  const navigate = useNavigate();
  const id = window.location.pathname.split("/")[3];
  const [data, setData] = useState(null);

  const load = () => axios.get(`${API}/courses/${id}`, { withCredentials: true }).then((r) => setData(r.data));
  useEffect(() => { load(); }, []);

  const submitForReview = async () => {
    await axios.post(`${API}/courses/${id}/submit`, {}, { withCredentials: true });
    toast.success("Submitted for review");
    load();
  };

  if (!data) return <div className="p-10">Loading…</div>;
  const c = data.course;

  return (
    <div className="space-y-8 max-w-4xl">
      <div className="flex items-center justify-between">
        <div>
          <div className="overline">Editing</div>
          <h1 className="font-heading text-3xl font-semibold text-[#0A231C]">{c.title}</h1>
        </div>
        <StatusBadge s={c.status} />
      </div>

      <div className="card-surface p-6 space-y-4">
        <div className="overline">Upload a lesson video (chunked)</div>
        <ChunkedUploader courseId={id} defaultOrder={data.videos.length} onUploaded={load} />
        <ul className="divide-y divide-[#F0EDE3] mt-2">
          {data.videos.map((v, i) => (
            <li key={v.id} className="py-3">
              <div className="flex justify-between text-sm">
                <span className="font-heading font-medium">{i + 1}. {v.title}</span>
                <span className="text-[#5C5C5C]">{((v.size || 0) / 1024 / 1024).toFixed(2)} MB</span>
              </div>
              <QuizBuilder videoId={v.id} videoTitle={v.title} onSaved={load} />
            </li>
          ))}
        </ul>
      </div>

      <div className="card-surface p-6">
        <div className="overline mb-2">Live webinars</div>
        <p className="text-sm text-[#5C5C5C]">
          Webinars are now standalone items with their own approval and pricing.
          {" "}
          <a href="/teacher/new-webinar" className="text-[#D45D3C] hover:underline">Plan a webinar →</a>
        </p>
      </div>

      {c.status === "draft" && (
        <button onClick={submitForReview} className="btn-primary" data-testid="submit-review-btn">Submit for IT Admin approval</button>
      )}

      <style>{`.input{width:100%;padding:0.6rem 0.85rem;border:1px solid #E5E3D8;border-radius:8px;background:white;outline:none;font-family:inherit;font-size:0.9rem}.input:focus{border-color:#0A231C}`}</style>
    </div>
  );
}
